import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import type { Projeto, ProjetoExtras } from '@/contexts/projeto-context';

type ModeloDocumento = 'arcelor' | 'praxis' | 'ar' | '';
type TipoProjeto = 'padrao' | 'budget' | 'manual_operacao';

interface ProjetoEditarModalProps {
  projeto: Projeto | null;
  onFechar: () => void;
  onSalvar: (id: string, nome: string, descricao?: string, clienteTipo?: Projeto['clienteTipo'], extras?: ProjetoExtras) => Promise<{ success: boolean; message?: string }>;
}

export function ProjetoEditarModal({ projeto, onFechar, onSalvar }: ProjetoEditarModalProps) {
  const { t } = useI18n();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [clienteTipo, setClienteTipo] = useState<ModeloDocumento>('arcelor');
  const [tipoProjeto, setTipoProjeto] = useState<TipoProjeto>('padrao');
  const [squadId, setSquadId] = useState('');
  const [budgetHabilitado, setBudgetHabilitado] = useState(false);
  const [budgetTotal, setBudgetTotal] = useState('');
  const [squads, setSquads] = useState<{ id: string; nome: string; regiao?: string | null }[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (projeto) {
      setNome(projeto.nome);
      setDescricao(projeto.descricao ?? '');
      setClienteTipo(
        projeto.clienteTipo === 'praxis' || projeto.clienteTipo === 'ar' || projeto.clienteTipo === 'arcelor'
          ? projeto.clienteTipo
          : projeto.clienteTipo ? 'arcelor' : ''
      );
      setTipoProjeto(
        projeto.tipoProjeto === 'budget' || projeto.tipoProjeto === 'manual_operacao'
          ? projeto.tipoProjeto
          : 'padrao'
      );
      setSquadId(projeto.squadId ?? '');
      setBudgetHabilitado(projeto.budgetHabilitado ?? false);
      setBudgetTotal(projeto.budgetTotal !== null && projeto.budgetTotal !== undefined ? String(projeto.budgetTotal) : '');
    }
  }, [projeto]);

  useEffect(() => {
    const loadSquads = async () => {
      const res = await apiService.listarSquads({ ativo: true });
      if (res.success && res.data) {
        setSquads(Array.isArray(res.data) ? res.data : []);
      }
    };
    loadSquads();
  }, []);

  if (!projeto) return null;

  const handleSubmit = async () => {
    if (!nome.trim()) return;
    setSalvando(true);
    try {
      const result = await onSalvar(projeto.id, nome.trim(), descricao.trim() || undefined, tipoProjeto === 'padrao' ? clienteTipo : '', {
        squadId: squadId || null,
        tipoProjeto,
        budgetHabilitado: tipoProjeto === 'budget' ? true : budgetHabilitado,
        budgetTotal: budgetTotal !== '' && !isNaN(Number(budgetTotal)) ? Number(budgetTotal) : null,
      });
      if (result.success) onFechar();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white mb-4">
          {t('projetos.editar.titulo')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
              {t('projetos.form.nome')}
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
              placeholder={t('projetos.form.nomePlaceholder')}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
              {t('projetos.form.descricao')}
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
              placeholder={t('projetos.form.descricaoPlaceholder')}
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
              {t('projetos.form.tipoProjeto')}
            </label>
            <select
              value={tipoProjeto}
              onChange={(e) => setTipoProjeto(e.target.value as TipoProjeto)}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
            >
              <option value="padrao">{t('projetos.form.tipoPadrao')}</option>
              <option value="budget">{t('projetos.form.tipoBudget')}</option>
              <option value="manual_operacao">{t('projetos.form.tipoManual')}</option>
            </select>
            {tipoProjeto === 'budget' && (
              <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {t('projetos.form.ajudaBudget')}
              </p>
            )}
            {tipoProjeto === 'manual_operacao' && (
              <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {t('projetos.form.ajudaManualEditar')}
              </p>
            )}
          </div>
          {tipoProjeto === 'padrao' && (
            <div>
              <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                {t('projetos.form.modeloDocumento')}
              </label>
              <select
                value={clienteTipo}
                onChange={(e) => setClienteTipo(e.target.value as ModeloDocumento)}
                className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
              >
                <option value="">{t('projetos.form.modeloNenhum')}</option>
                <option value="arcelor">Arcelor</option>
                {/* Praxis e AR Blueprint saíram da escolha por enquanto: só
                    aparecem no projeto que já usa o modelo, para que salvar uma
                    edição não troque o documento dele sem querer. Para
                    reativar, devolva as <option> fixas aqui e no modal de
                    criação (projeto-selecao). */}
                {clienteTipo === 'praxis' && <option value="praxis">Praxis</option>}
                {clienteTipo === 'ar' && <option value="ar">AR Blueprint</option>}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
              {t('projetos.form.squad')}
            </label>
            <select
              value={squadId}
              onChange={(e) => setSquadId(e.target.value)}
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
            >
              <option value="">{t('projetos.form.squadNenhum')}</option>
              {squads.map((sq) => (
                <option key={sq.id} value={sq.id}>{sq.nome}{sq.regiao ? ` — ${sq.regiao}` : ''}</option>
              ))}
            </select>
          </div>
          <div className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg p-3 space-y-3">
            {tipoProjeto === 'padrao' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="budget-habilitado"
                  checked={budgetHabilitado}
                  onChange={(e) => setBudgetHabilitado(e.target.checked)}
                  className="rounded border-schmersal-gray-light dark:border-schmersal-gray-dark"
                />
                <label htmlFor="budget-habilitado" className="text-sm font-medium text-schmersal-gray-dark dark:text-white">
                  {t('projetos.form.budgetHabilitar')}
                </label>
              </div>
            )}
            {(budgetHabilitado || tipoProjeto === 'budget') && (
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  {t('projetos.form.budgetTotal')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={budgetTotal}
                  onChange={(e) => setBudgetTotal(e.target.value)}
                  placeholder={t('projetos.form.budgetTotalPlaceholder')}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onFechar}
            className="px-4 py-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-blue rounded-lg transition-colors"
            disabled={salvando}
          >
            {t('comum.acao.cancelar')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!nome.trim() || salvando}
            className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {salvando ? t('comum.acao.salvando') : t('comum.acao.salvar')}
          </button>
        </div>
      </div>
    </div>
  );
}
